/**
 * LiveBoardScreen — Live Board / Fase 6 · Execução & Revisão (F3-FE-1).
 *
 * Kanban 8 colunas completo portado do protótipo, alimentado por:
 *   1. GET /executions/{id}/board  → estado inicial / rehidratação
 *   2. SSE /executions/{id}/stream → eventos kanban ao vivo (movimentação de cards)
 *
 * Colunas (KANBAN_COLUMNS — fiel ao protótipo):
 *   Now · Planning · ToDo · Backing coding · Coding · Backing QA · Testing · Done
 *
 * Tipos de evento SSE: cli · kanban · metrics · gate
 *
 * Layout fiel ao protótipo:
 *   Topbar       — projeto, badge de ciclo, tokens/custo ao vivo, estado conexão
 *   Barra global — progresso + strip de ciclos
 *   Corpo        — COLUNA ÚNICA: Kanban 8 colunas + CLI streaming (log ao vivo),
 *                  ocupando a largura inteira. As colunas laterais ("Executing Flow" e
 *                  métricas) saíram em 2026-09-22 por decisão do dono; custo e tokens
 *                  continuam na topbar.
 *
 * CardDetailModal — modal ao clicar num card (testes, cobertura, critérios).
 * CompletionModal — exibido quando execução conclui (ver F3-FE-2 — importado).
 *
 * Segurança (OWASP A09):
 *   - NUNCA exibe ou loga wallet_txn_id (já filtrado em api/stream.js)
 *   - 401/403 exibe mensagem amigável sem vazar detalhes internos
 *
 * Acessibilidade:
 *   - aria-live="polite" no log de CLI
 *   - aria-live="assertive" para gate pendente
 *   - prefers-reduced-motion: desativa animações de pulse/blink
 *   - Foco de teclado visível em todos os botões
 *
 * Standards: NFR, Clean, OWASP A09, prefers-reduced-motion.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CycleStrip } from './liveboard/UnifiedGatesPanel';
import { useBacklog } from '@/api/backlog';
import {
  Terminal,
  Radio,
  AlertTriangle,
  CheckCircle,
  Loader,
  WifiOff,
  Wifi,
  RotateCcw,
  ShieldAlert,
  Activity,
  Cpu,
  Layers,
  GitBranch,
  Play,
  X,
  CheckSquare,
  Clock,
  Box,
  Square,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, StatusDot } from '@/components/ui';
import { ForbiddenBanner } from '@/components/ui';
import {
  useExecution, useExecutionBoard, useExecutionArtifacts,
  useExecutionGateA, useExecutionGateB, executionKeys, useCancelExecution,
} from '@/api/executions';
import { useExecutionStream } from '@/api/stream';
import { useNavigationContext } from '@/navigation';
import { SaudeDaExecucao } from '@/components/SaudeDaExecucao';
import { useSaudeDaExecucao } from '@/api/saudeDaExecucao';

// ─── Constantes — colunas do Kanban (fiel ao protótipo) ───────────────────────

/**
 * KANBAN_COLUMNS — 8 colunas na ordem exata do protótipo.
 * id: chave usada nos eventos SSE (payload.to / payload.column).
 */
export const KANBAN_COLUMNS = [
  { id: 'now',          label: 'Now',          color: C.cyber     },
  { id: 'planning',     label: 'Planning',     color: C.neon      },
  { id: 'todo',         label: 'ToDo',         color: C.neutralDim},
  { id: 'backingCoding',label: 'Backing coding',color: C.quantum  },
  { id: 'coding',       label: 'Coding',       color: C.cyber     },
  { id: 'backingQA',    label: 'Backing QA',   color: C.digital   },
  { id: 'testing',      label: 'Testing',      color: C.neon      },
  { id: 'done',         label: 'Done',         color: C.cyberDim  },
];

// Gate decidido → encerra o polling daquele gate. Execução terminal → encerra
// qualquer polling. (status do backend: gate = pending|requesting|escalated|
// approved|rejected|clarify · execução = running|completed|failed|…)
const GATE_DECIDED = new Set(['approved', 'rejected']);
export const EXEC_TERMINAL = new Set([
  'completed', 'done', 'succeeded', 'success', 'failed', 'error', 'canceled', 'cancelled',
  // Encerramentos controlados do workflow: sem eles o Live Board seguia consultando uma
  // execução que já terminou. `contract_blocked` = modo estrito com bloqueio (Onda 2).
  'gate_rejected', 'sla_expired', 'clarify_cap_exceeded', 'contract_blocked',
]);

const ROTULO_DO_STATUS = {
  running: 'Em execução',
  completed: 'Concluído',
  failed: 'Falhou',
  failed_gate: 'Falhou',
  gate_waiting: 'Aguardando gate',
  contract_blocked: 'Bloqueado pelo contrato',
  gate_rejected: 'Gate rejeitado',
  sla_expired: 'Prazo do gate expirado',
  clarify_cap_exceeded: 'Limite de esclarecimentos',
};

/** Rótulo pt-BR do status da execução (status desconhecido aparece como veio). */
export function rotuloDoStatus(status) {
  return ROTULO_DO_STATUS[status] ?? status;
}

/**
 * gatePollInterval — intervalo (ms) de polling de um gate no Live Board, ou
 * `false` para parar. É o FALLBACK para quando o evento SSE não chega (stream
 * bufferizado por proxy/URL pública): pollar ENQUANTO a execução está ativa
 * descobre um gate recém-'pending' sem depender do push. Para quando o gate é
 * decidido (approved/rejected) OU a execução termina — sem polling à toa.
 *
 * @param {string|null|undefined} gateStatus  status do gate consultado
 * @param {string|null|undefined} execStatus  status da execução
 * @returns {number|false}
 */
export function gatePollInterval(gateStatus, execStatus) {
  const g = String(gateStatus ?? '').toLowerCase();
  const e = String(execStatus ?? '').toLowerCase();
  if (GATE_DECIDED.has(g)) return false;
  if (EXEC_TERMINAL.has(e)) return false;
  return 8_000;
}

/** Normaliza a chave de coluna vinda do SSE para os ids do KANBAN_COLUMNS. */
function normalizeColumnKey(raw) {
  if (!raw) return null;
  const map = {
    // SSE pode vir em snake_case ou camelCase
    now:            'now',
    planning:       'planning',
    todo:           'todo',
    backing_coding: 'backingCoding',
    backingcoding:  'backingCoding',
    backingCoding:  'backingCoding',
    coding:         'coding',
    backing_qa:     'backingQA',
    backingqa:      'backingQA',
    backingQA:      'backingQA',
    testing:        'testing',
    done:           'done',
  };
  return map[raw] ?? map[raw.toLowerCase()] ?? raw;
}

// ─── Nós do fluxo (EXECUTING FLOW) ────────────────────────────────────────────

const FLOW_NODES = [
  { id: 'input',          label: 'Input',          icon: GitBranch, tier: 'INPUT'        },
  { id: 'product-owner',  label: 'Product Owner',  icon: Cpu,       tier: 'ORQUESTRADOR' },
  { id: 'tech-lead',      label: 'Tech Lead',      icon: Cpu,       tier: 'ORQUESTRADOR' },
  { id: 'squad-fundacao', label: 'Squad Fundação',  icon: Layers,    tier: 'SQUAD'        },
  { id: 'squad-cadastro', label: 'Squad Cadastro',  icon: Layers,    tier: 'SQUAD'        },
  { id: 'squad-canais',   label: 'Squad Canais',    icon: Layers,    tier: 'SQUAD'        },
  { id: 'squad-qualidade',label: 'Squad Qualidade', icon: Layers,    tier: 'SQUAD'        },
  { id: 'output',         label: 'Pull Request',    icon: CheckCircle,tier: 'OUTPUT'      },
];

// ─── useReducedMotion ─────────────────────────────────────────────────────────

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

// ─── Kanban state management ──────────────────────────────────────────────────

/**
 * Constrói o estado inicial do board a partir dos dados de GET /board.
 * Garante que todas as 8 colunas existem, mesmo que vazias.
 */
function buildInitialBoard(boardData) {
  const empty = Object.fromEntries(KANBAN_COLUMNS.map((c) => [c.id, []]));
  if (!boardData?.columns) return empty;

  const normalized = { ...empty };
  for (const [rawKey, cards] of Object.entries(boardData.columns)) {
    const key = normalizeColumnKey(rawKey);
    if (key && Array.isArray(cards)) {
      normalized[key] = cards;
    }
  }
  return normalized;
}

// Cor por tipo de item (fiel ao protótipo: STORY cyber · TASK digital).
const CARD_TYPE = {
  story: { label: 'STORY', color: C.cyber },
  task:  { label: 'TASK',  color: C.digital },
};

/**
 * seedBoardFromBacklog — quando o board da execução ainda está vazio (a geração
 * de código não emitiu eventos), mostra os itens do Product Backlog aprovado como
 * cards em "ToDo" — assim o Kanban nunca aparece vazio e reflete o escopo REAL do
 * projeto (igual ao protótipo). Quando os eventos SSE chegam, o board ao vivo
 * assume (ver displayColumns).
 */
function seedBoardFromBacklog(backlog) {
  const empty = Object.fromEntries(KANBAN_COLUMNS.map((c) => [c.id, []]));
  const epics = backlog?.epics ?? [];
  const todo = [];
  epics.forEach((ep) =>
    (ep.features ?? []).forEach((f) =>
      (f.stories ?? []).forEach((s) =>
        todo.push({
          card_id: s.id,
          title: s.title,
          type: s.type === 'task' ? 'task' : 'story',
          squad: ep.title,
          points: s.points,
          reuse: s.reuse,
        }),
      ),
    ),
  );
  return { ...empty, todo };
}

/**
 * Aplica um evento SSE kanban ao estado do board.
 * Evento esperado: { payload: { card_id, from_column, to_column, card?: {...} } }
 */
function applyKanbanEvent(columns, event) {
  const { card_id, from_column, to_column, card } = event.payload ?? {};
  if (!card_id || !to_column) return columns;

  const from = normalizeColumnKey(from_column);
  const to   = normalizeColumnKey(to_column);
  if (!to) return columns;

  const next = { ...columns };

  // Remove o card da coluna de origem (se especificada)
  if (from && next[from]) {
    next[from] = next[from].filter((c) => c.card_id !== card_id);
  } else {
    // Procura o card em todas as colunas para movê-lo
    for (const colId of Object.keys(next)) {
      const idx = next[colId].findIndex((c) => c.card_id === card_id);
      if (idx !== -1) {
        next[colId] = [...next[colId]];
        next[colId].splice(idx, 1);
        break;
      }
    }
  }

  // Garante que a coluna de destino existe
  if (!next[to]) next[to] = [];

  // Adiciona ou atualiza o card na coluna de destino
  const existingIdx = next[to].findIndex((c) => c.card_id === card_id);
  if (existingIdx !== -1) {
    next[to] = [...next[to]];
    next[to][existingIdx] = card ?? { card_id, ...next[to][existingIdx] };
  } else {
    next[to] = [...next[to], card ?? { card_id, title: card_id, status: to }];
  }

  return next;
}

// ─── CardDetailModal ──────────────────────────────────────────────────────────

/**
 * CardDetailModal — modal de detalhes de um card do Kanban.
 * Portado do protótipo: exibe descrição, critérios/tarefas, testes e cobertura.
 * Testes só aparecem quando o card está em 'testing' ou 'done'.
 */
function CardDetailModal({ card, columnId, onClose }) {
  const reducedMotion = useReducedMotion();
  const closeRef = useRef(null);

  // Foca no botão de fechar ao abrir
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Fecha com Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const colMeta = KANBAN_COLUMNS.find((c) => c.id === columnId);
  const showTests = columnId === 'testing' || columnId === 'done';

  const statusLabel = {
    now:           'Em foco agora',
    planning:      'Planejamento',
    todo:          'A fazer',
    backingCoding: 'Aguardando codificação',
    coding:        'Em desenvolvimento',
    backingQA:     'Aguardando QA',
    testing:       'Em teste',
    done:          'Concluído',
  }[columnId] ?? columnId;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="card-modal-title"
      style={{
        position:        'fixed',
        inset:           0,
        zIndex:          9999,
        backgroundColor: 'rgba(12,12,12,0.88)',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         '16px',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          backgroundColor: C.blackElev,
          border:          `1px solid ${C.blackBorder}`,
          borderRadius:    12,
          width:           '100%',
          maxWidth:        520,
          maxHeight:       '85vh',
          overflowY:       'auto',
          animation:       reducedMotion ? 'none' : 'navaBlink 120ms ease-out 1',
        }}
      >
        {/* Header */}
        <div style={{
          display:         'flex',
          alignItems:      'flex-start',
          gap:             12,
          padding:         '16px 20px',
          borderBottom:    `1px solid ${C.blackBorder}`,
          position:        'sticky',
          top:             0,
          backgroundColor: C.blackElev,
          zIndex:          1,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{
              display:    'flex',
              alignItems: 'center',
              gap:        8,
              flexWrap:   'wrap',
              marginBottom: 4,
            }}>
              <span id="card-modal-title" style={{
                fontSize:   14,
                fontWeight: 700,
                color:      C.neutral,
              }}>
                {card.title ?? card.card_id}
              </span>
              {colMeta && (
                <span style={{
                  fontSize:        10,
                  fontWeight:      700,
                  padding:         '2px 7px',
                  borderRadius:    4,
                  backgroundColor: `${colMeta.color}22`,
                  border:          `1px solid ${colMeta.color}44`,
                  color:           colMeta.color,
                  letterSpacing:   '0.04em',
                  textTransform:   'uppercase',
                }}>
                  {statusLabel}
                </span>
              )}
            </div>
            {card.agent_slug && (
              <div style={{ fontSize: 11, color: C.neutralMuted }}>
                Agent: <span style={{ color: C.cyber, fontFamily: 'monospace' }}>{card.agent_slug}</span>
              </div>
            )}
          </div>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label="Fechar detalhes do card"
            style={{
              background:   'transparent',
              border:       `1px solid ${C.blackBorder}`,
              borderRadius: 6,
              color:        C.neutralMuted,
              cursor:       'pointer',
              padding:      '4px 6px',
              flexShrink:   0,
              display:      'flex',
              alignItems:   'center',
            }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Status do card */}
          {card.status && card.status !== columnId && (
            <div style={{
              fontSize:        11,
              color:           C.neutralMuted,
              padding:         '6px 10px',
              backgroundColor: C.blackElev2,
              borderRadius:    6,
              border:          `1px solid ${C.blackBorder}`,
            }}>
              Status do card: <span style={{ color: C.neutralDim }}>{card.status}</span>
            </div>
          )}

          {/* Testes — só em testing / done */}
          {showTests && card.tests && card.tests.length > 0 && (
            <section aria-labelledby="card-tests-heading">
              <h3
                id="card-tests-heading"
                style={{
                  fontSize:      10,
                  fontWeight:    700,
                  color:         C.neutralMuted,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  marginBottom:  8,
                  margin:        '0 0 8px',
                }}
              >
                Testes
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {card.tests.map((test, i) => (
                  <div
                    key={test.test_id ?? i}
                    style={{
                      display:         'flex',
                      alignItems:      'flex-start',
                      gap:             8,
                      padding:         '8px 10px',
                      borderRadius:    6,
                      backgroundColor: C.blackElev2,
                      border:          `1px solid ${C.blackBorder}`,
                    }}
                  >
                    {test.status === 'passed'
                      ? <CheckCircle size={13} aria-hidden="true" style={{ color: C.cyber, flexShrink: 0, marginTop: 1 }} />
                      : test.status === 'failed'
                        ? <X size={13} aria-hidden="true" style={{ color: C.hot, flexShrink: 0, marginTop: 1 }} />
                        : <Clock size={13} aria-hidden="true" style={{ color: C.neutralMuted, flexShrink: 0, marginTop: 1 }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: C.neutral, lineHeight: 1.4 }}>
                        {test.name ?? test.test_id}
                      </div>
                      {test.duration_ms != null && (
                        <div style={{ fontSize: 10, color: C.neutralMuted, marginTop: 2 }}>
                          {test.duration_ms}ms
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize:        9,
                      fontWeight:      700,
                      padding:         '2px 6px',
                      borderRadius:    4,
                      backgroundColor: test.status === 'passed' ? `${C.cyber}22` : test.status === 'failed' ? `${C.hot}22` : `${C.neutralMuted}22`,
                      color:           test.status === 'passed' ? C.cyber : test.status === 'failed' ? C.hot : C.neutralMuted,
                      textTransform:   'uppercase',
                      letterSpacing:   '0.04em',
                    }}>
                      {test.status === 'passed' ? 'PASSOU' : test.status === 'failed' ? 'FALHOU' : 'PENDENTE'}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {showTests && (!card.tests || card.tests.length === 0) && (
            <div style={{
              textAlign:       'center',
              fontSize:        12,
              color:           C.neutralMuted,
              padding:         '20px',
              border:          `1px dashed ${C.blackBorder}`,
              borderRadius:    8,
            }}>
              {columnId === 'testing' ? 'Executando testes...' : 'Nenhum teste registrado.'}
            </div>
          )}

          {/* Informações extras do card */}
          {card.card_id && (
            <div style={{
              fontSize:  10,
              color:     C.neutralMuted,
              fontFamily:'monospace',
              paddingTop: 8,
              borderTop:  `1px solid ${C.blackBorder}`,
            }}>
              card_id: {card.card_id}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function ConnectionIndicator({ state }) {
  const reducedMotion = useReducedMotion();
  const meta = {
    connecting:   { label: 'CONECTANDO...',  color: C.neutralMuted, Icon: Loader,   pulse: true  },
    connected:    { label: 'CONECTADO',       color: C.cyber,       Icon: Wifi,     pulse: false },
    reconnecting: { label: 'RECONECTANDO...', color: C.digital,     Icon: RotateCcw,pulse: true  },
    closed:       { label: 'DESCONECTADO',    color: C.hot,         Icon: WifiOff,  pulse: false },
  };
  const m = meta[state] ?? meta.connecting;
  const Icon = m.Icon;

  return (
    <span
      role="status"
      aria-label={`Status da conexão: ${m.label}`}
      style={{
        display:         'inline-flex',
        alignItems:      'center',
        gap:             5,
        fontSize:        10,
        fontWeight:      700,
        letterSpacing:   '0.06em',
        color:           m.color,
        padding:         '3px 8px',
        borderRadius:    4,
        backgroundColor: `${m.color}18`,
        border:          `1px solid ${m.color}44`,
        animation:       (m.pulse && !reducedMotion) ? 'navaPulse 2s ease-out infinite' : 'none',
      }}
    >
      <Icon size={11} aria-hidden="true" />
      {m.label}
    </span>
  );
}

/** CliLog — log de CLI ao vivo com scroll automático. */
function CliLog({ lines, connectionState }) {
  const endRef = useRef(null);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'end' });
  }, [lines, reducedMotion]);

  const live = connectionState === 'connected';

  return (
    <div style={{
      flex:            1,
      display:         'flex',
      flexDirection:   'column',
      minHeight:       0,
      borderRadius:    10,
      border:          `1px solid ${C.blackBorder}`,
      overflow:        'hidden',
      backgroundColor: C.black,
    }}>
      {/* Header do terminal — identidade + "ao vivo" */}
      <div style={{
        display:         'flex',
        alignItems:      'center',
        gap:             9,
        padding:         '9px 14px',
        backgroundColor: C.blackElev,
        borderBottom:    `1px solid ${C.blackBorder}`,
        flexShrink:      0,
      }}>
        <Terminal size={13} aria-hidden="true" style={{ color: C.cyber }} />
        <span style={{ fontSize: 11.5, fontWeight: 700, color: C.neutral, letterSpacing: '0.03em' }}>
          Logs de Execução
        </span>
        <span aria-hidden="true" style={{
          width:           7,
          height:          7,
          borderRadius:    '50%',
          backgroundColor: live ? C.cyber : C.neutralMuted,
          boxShadow:       live ? `0 0 8px ${C.cyber}` : 'none',
          animation:       (live && !reducedMotion) ? 'navaLiveDot 1.4s ease-in-out infinite' : 'none',
        }} />
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          color: live ? C.cyber : C.neutralMuted }}>
          {live ? 'AO VIVO' : 'OFFLINE'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: C.neutralMuted,
          fontVariantNumeric: 'tabular-nums' }}>
          {lines.length} linha{lines.length === 1 ? '' : 's'}
        </span>
      </div>

      {/* Corpo do log — protagonista, ocupa todo o espaço */}
      <div
        role="log"
        aria-live="polite"
        aria-label="Log de execução ao vivo"
        aria-atomic="false"
        aria-relevant="additions"
        style={{
          flex:       1,
          overflowY:  'auto',
          fontFamily: '"JetBrains Mono", "Fira Code", "Cascadia Code", monospace',
          fontSize:   12,
          lineHeight: 1.65,
          padding:    '12px 14px',
          minHeight:  220,
          // Sem maxHeight fixo: o corpo do log preenche o espaço restante (flex:1
          // numa cadeia agora limitada ao viewport) e rola INTERNAMENTE, ajustando
          // a qualquer tamanho de tela.
        }}
      >
        {lines.length === 0
          ? <span style={{ color: C.neutralMuted }}>Aguardando o stream de execução…</span>
          : lines.map((line, i) => <CliLine key={i} line={line} index={i} animate={!reducedMotion} />)}
        <div ref={endRef} aria-hidden="true" />
      </div>
    </div>
  );
}

/**
 * A5 (FR-5b): uma linha de delegação ("↪ A → B · …" / "✓ A → B · concluído (N s)")
 * — formatada por `formatDelegacaoLine` em `@/api/stream`. Devolve as partes ou null.
 */
export function partesDaDelegacao(rest) {
  const m = String(rest ?? '').match(/^([↪✓]) (\S+) → (\S+)(?: · ([\s\S]*))?$/);
  if (!m) return null;
  return { fim: m[1] === '✓', de: m[2], para: m[3], detalhe: m[4] ?? '' };
}

// ── Tom da linha do log ──────────────────────────────────────────────────────
// Execução c9b11148 (2026-10-06): o pulso "⏳ Etapa 2/3 … últimos: tests/…/
// test_req_erros_rate_passos.py" saiu VERMELHO porque a regra era `includes('erro')`.
// Quem manda é o MARCADOR que a plataforma põe no começo da mensagem; a palavra solta só
// vale sem marcador, inteira, fora de caminho de arquivo e sem contagem zerada.
const MARCADORES_DO_TOM = [
  ['erro',    ['❌', '⛔', '✗', '✖', '🛑']],
  ['aviso',   ['⚠', '🔁', '⚡', '⚪']],
  ['sucesso', ['✓', '✅', '✔']],
  ['neutro',  ['⏳', '🧱', '▶', '↪']],
];
const CAMINHO_OU_CODIGO = /`[^`]*`|\S*[\\/]\S*|\S+\.[A-Za-z][A-Za-z0-9]{0,5}(?=[\s,;:)]|$)/g;
const PALAVRA_DE_ERRO   = /(^|[^\p{L}\p{N}_])(erros?|falhou|falharam)(?![\p{L}\p{N}_])/iu;
const ERRO_NEGADO       = /(^|[^\p{L}\p{N}_])(0|zero|sem|nenhum)\s+erros?(?![\p{L}\p{N}_])/giu;

/** 'erro' | 'aviso' | 'sucesso' | 'neutro' para o texto de uma linha (sem o prefixo do agente). */
export function tomDaLinha(texto) {
  const t = String(texto ?? '').trimStart();
  if (!t) return 'neutro';
  for (const [tom, marcadores] of MARCADORES_DO_TOM) {
    if (marcadores.some((m) => t.startsWith(m))) return tom;
  }
  if (t.includes('Checkpoint')) return 'aviso';
  const prosa = t.replace(CAMINHO_OU_CODIGO, ' ').replace(ERRO_NEGADO, ' ');
  if (PALAVRA_DE_ERRO.test(prosa)) return 'erro';
  if (t.includes('concluído') || t.includes('aprovado')) return 'sucesso';
  return 'neutro';
}

export function CliLine({ line, index = 0, animate = false }) {
  const match = line.match(/^\[([^\]]+)\]\s/);
  const prefix = match ? match[0] : '';
  const rest   = match ? line.slice(prefix.length) : line;
  const delegacao = partesDaDelegacao(rest);
  const agentColors = {
    'input':             C.cyber,
    'tech-lead':         C.neon,
    'product-owner':     C.digital,
    'backend-architect': C.quantum,
    'csharp-pro':        C.cyberDim,
    'react-pro':         C.cyber,
    'postgres-pro':      C.neon,
    'security-architect':C.hot,
    'security-auditor':  C.hotLight,
    'qa-engineer':       C.cyberDim,
    'test-automator':    C.cyberDim,
    'ux-designer':       C.digitalLight,
    'frontend-architect':C.digitalLight,
  };
  const agent       = match?.[1] ?? '';
  const prefixColor = agentColors[agent] ?? C.neutralMuted;
  const restColor   = { erro: C.hot, aviso: C.digital, sucesso: C.neutral,
                        neutro: C.neutralDim }[tomDaLinha(rest)];

  return (
    <div style={{
      display:    'flex',
      gap:        12,
      wordBreak:  'break-word',
      animation:  animate ? 'navaLogIn 0.22s ease-out' : 'none',
    }}>
      <span aria-hidden="true" style={{
        flexShrink:         0,
        width:              28,
        textAlign:          'right',
        color:              C.neutralMuted,
        opacity:            0.45,
        userSelect:         'none',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {index + 1}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ color: prefixColor, flexShrink: 0, fontWeight: 600 }}>{prefix}</span>
        {delegacao ? (
          <span
            data-testid="cli-delegacao"
            title={delegacao.fim
              ? `${delegacao.para} concluiu o que ${delegacao.de} delegou`
              : `${delegacao.de} delegou a ${delegacao.para}`}
            style={{ color: delegacao.fim ? C.neutral : C.neutralDim }}
          >
            <span aria-hidden="true">{delegacao.fim ? '✓' : '↪'} </span>
            <span style={{ color: C.cyber, fontWeight: 600 }}>{delegacao.de}</span>
            <span style={{ color: C.neutralMuted }}> → </span>
            <span style={{ color: C.digitalLight, fontWeight: 600 }}>{delegacao.para}</span>
            {delegacao.detalhe ? <span> · {delegacao.detalhe}</span> : null}
          </span>
        ) : (
          <span style={{ color: restColor }}>{rest}</span>
        )}
      </span>
    </div>
  );
}

/** Ação do artefato → rótulo/cor (A=add, M=modify, D=delete). */
const ARTIFACT_ACTION = {
  A: { label: 'A', color: C.neon,    title: 'Adicionado' },
  M: { label: 'M', color: C.cyber,   title: 'Modificado' },
  D: { label: 'D', color: C.digital, title: 'Removido' },
};

/** Renderiza um diff unificado com cores por linha (+ verde / − rosa). */
function DiffView({ diff }) {
  const text = typeof diff === 'string' ? diff : (diff ? JSON.stringify(diff, null, 2) : '');
  if (!text) return <div style={{ fontSize: 10, color: C.neutralMuted, padding: '6px 8px' }}>Sem diff disponível.</div>;
  const lines = text.split('\n').slice(0, 400);
  return (
    <pre style={{ margin: 0, padding: '8px 10px', background: C.black, borderRadius: 6,
      fontSize: 10.5, lineHeight: 1.5, overflowX: 'auto', fontFamily: 'monospace',
      border: `1px solid ${C.blackBorder}` }}>
      {lines.map((ln, i) => {
        const isAdd = ln.startsWith('+') && !ln.startsWith('+++');
        const isDel = ln.startsWith('-') && !ln.startsWith('---');
        const isHunk = ln.startsWith('@@');
        const color = isAdd ? C.neon : isDel ? C.digital : isHunk ? C.cyber : C.neutralDim;
        return <div key={i} style={{ color, whiteSpace: 'pre' }}>{ln || ' '}</div>;
      })}
    </pre>
  );
}

/**
 * CodeViewer — arquivos gerados com +/− linhas (a partir de code_artifacts).
 * Clicar num arquivo expande o diff unificado. Escopo: Live Board · Code Viewer.
 */
function CodeViewer({ executionId }) {
  const { data, isLoading } = useExecutionArtifacts(executionId, 50, 0);
  const [openId, setOpenId] = useState(null);
  const artifacts = data?.code_artifacts ?? [];
  const total = data?.artifacts_total ?? artifacts.length;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 9, fontWeight: 700, color: C.neutralMuted,
          letterSpacing: '0.1em', textTransform: 'uppercase' }}>Code Viewer</span>
        {total > 0 && <Badge color={C.cyber} light style={{ fontSize: 9 }}>{total} arquivos</Badge>}
      </div>

      {isLoading && <div style={{ fontSize: 11, color: C.neutralMuted }}>Carregando arquivos…</div>}
      {!isLoading && artifacts.length === 0 && (
        <div style={{ fontSize: 11, color: C.neutralMuted }}>Nenhum arquivo gerado ainda.</div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {artifacts.map((a) => {
          const act = ARTIFACT_ACTION[a.action] ?? ARTIFACT_ACTION.M;
          const id = a.artifact_id ?? a.path;
          const open = openId === id;
          return (
            <div key={id} style={{ border: `1px solid ${C.blackBorder}`, borderRadius: 7,
              background: C.blackElev, overflow: 'hidden' }}>
              <button
                onClick={() => setOpenId(open ? null : id)}
                title={a.path}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '7px 8px', background: 'transparent', border: 'none', cursor: 'pointer',
                  textAlign: 'left' }}
              >
                <span style={{ width: 16, height: 16, borderRadius: 4, flexShrink: 0, fontSize: 9,
                  fontWeight: 700, color: act.color, background: `${act.color}22`,
                  display: 'grid', placeItems: 'center' }} title={act.title}>{act.label}</span>
                <span style={{ flex: 1, minWidth: 0, fontFamily: 'monospace', fontSize: 10.5,
                  color: C.neutralDim, whiteSpace: 'nowrap', overflow: 'hidden',
                  textOverflow: 'ellipsis', direction: 'rtl' }}>{a.path}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: C.neon }}>+{a.additions ?? 0}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: C.digital }}>−{a.deletions ?? 0}</span>
                {a.cycle_number != null && (
                  <span style={{ fontFamily: 'monospace', fontSize: 9, color: C.neutralMuted }}>c{a.cycle_number}</span>
                )}
              </button>
              {open && <DiffView diff={a.diff} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * SandboxIndicator — indicador de sandbox real no Live Board (F4-FE-3).
 *
 * Exibe badge/status do sandbox quando um evento SSE do tipo 'sandbox' é recebido.
 * Valores seguros de exibir: status ('spawned'|'ready'|'terminated'), is_real, ttl_sec.
 * NUNCA exibe sandbox_run_id, IPs internos ou dados sensíveis de infraestrutura.
 *
 * Quando is_real=true  → badge "SANDBOX REAL" (cyber)
 * Quando is_real=false → badge "SIMULADO" (digital)
 *
 * @param {{ sandboxEvent: import('../api/stream').SseEvent|null }} props
 */
function SandboxIndicator({ sandboxEvent }) {
  const reducedMotion = useReducedMotion();

  if (!sandboxEvent) return null;

  const { status, is_real, ttl_sec } = sandboxEvent.payload ?? {};

  const isActive   = status === 'spawned' || status === 'ready';
  const isReal     = Boolean(is_real);
  const accentColor = isReal ? C.cyber : C.digital;

  const statusLabel = {
    spawned:    'Inicializando',
    ready:      'Ativo',
    terminated: 'Encerrado',
  }[status] ?? status ?? 'Ativo';

  return (
    <div
      role="status"
      aria-label={`Sandbox ${isReal ? 'real' : 'simulado'}: ${statusLabel}`}
      style={{
        display:         'flex',
        alignItems:      'center',
        gap:             8,
        padding:         '10px 12px',
        borderRadius:    6,
        backgroundColor: `${accentColor}12`,
        border:          `1px solid ${accentColor}33`,
        animation:       (isActive && !reducedMotion) ? 'navaPulse 2.5s ease-out infinite' : 'none',
      }}
    >
      <Box size={13} aria-hidden="true" style={{ color: accentColor, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize:      10,
          fontWeight:    700,
          color:         accentColor,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          lineHeight:    1.3,
        }}>
          {isReal ? 'Sandbox Real' : 'Sandbox Simulado'}
        </div>
        <div style={{
          fontSize:  10,
          color:     C.neutralMuted,
          marginTop: 2,
        }}>
          {statusLabel}
          {ttl_sec != null && isActive && (
            <span> · TTL {ttl_sec}s</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ progress }) {
  const pct = Math.min(100, Math.max(0, progress ?? 0));
  const reducedMotion = useReducedMotion();
  return (
    <div
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Progresso da execução: ${pct}%`}
      style={{
        width:           '100%',
        height:          4,
        backgroundColor: C.blackElev2,
        borderRadius:    2,
        overflow:        'hidden',
      }}
    >
      <div
        style={{
          width:           `${pct}%`,
          height:          '100%',
          backgroundColor: C.cyber,
          borderRadius:    2,
          transition:      reducedMotion ? 'none' : 'width 600ms ease',
          boxShadow:       `0 0 8px ${C.cyber}66`,
        }}
      />
    </div>
  );
}

function StreamErrorBanner({ error, onRetry }) {
  if (!error) return null;
  if (error.isForbidden) {
    return (
      <ForbiddenBanner
        detail={error.status === 401
          ? 'Sua sessão expirou. Faça login novamente para continuar acompanhando a execução.'
          : 'Você não tem permissão para acompanhar esta execução.'}
      />
    );
  }
  return (
    <div
      role="alert"
      style={{
        display:         'flex',
        alignItems:      'flex-start',
        gap:             10,
        padding:         '12px 16px',
        borderRadius:    8,
        backgroundColor: `${C.hot}11`,
        border:          `1px solid ${C.hot}33`,
        marginBottom:    16,
      }}
    >
      <AlertTriangle size={15} aria-hidden="true" style={{ color: C.hot, flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.hot, marginBottom: 2 }}>Erro na conexão do stream</div>
        <div style={{ fontSize: 12, color: C.neutralDim }}>{error.message}</div>
      </div>
      {onRetry && (
        <Btn variant="ghost" size="sm" onClick={onRetry} icon={RotateCcw}>Reconectar</Btn>
      )}
    </div>
  );
}

// ─── Kanban Board completo (8 colunas) ────────────────────────────────────────

/**
 * KanbanCard — card individual dentro de uma coluna.
 */
function KanbanCard({ card, columnId, onClick }) {
  const reducedMotion = useReducedMotion();
  const col = KANBAN_COLUMNS.find((c) => c.id === columnId);
  const isActive = columnId === 'coding' || columnId === 'testing';
  const type = CARD_TYPE[card.type === 'task' ? 'task' : 'story'];

  return (
    <button
      onClick={() => onClick(card, columnId)}
      aria-label={`Ver detalhes: ${card.title ?? card.card_id}`}
      style={{
        width:           '100%',
        textAlign:       'left',
        background:      C.blackElev2,
        border:          `1px solid ${isActive ? `${col?.color ?? C.blackBorder}44` : C.blackBorder}`,
        borderLeft:      `2px solid ${type.color}`,
        borderRadius:    6,
        padding:         '8px 10px',
        cursor:          'pointer',
        transition:      reducedMotion ? 'none' : 'border-color 150ms, background 150ms',
        animation:       (isActive && !reducedMotion) ? 'navaBlink 2s ease-in-out infinite' : 'none',
        marginBottom:    4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 8, fontWeight: 700, letterSpacing: '0.05em',
          color: type.color, background: `${type.color}1F`, borderRadius: 4, padding: '1px 5px' }}>
          {type.label}
        </span>
        {card.reuse && <span style={{ fontSize: 9, color: C.quantum }} title="coberto por reuso">♻</span>}
        {card.points ? <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 8.5, color: C.neutralMuted }}>{card.points} pts</span> : null}
      </div>
      <div style={{
        fontSize:      11,
        fontWeight:    600,
        color:         C.neutral,
        lineHeight:    1.4,
        marginBottom:  (card.agent_slug || card.squad) ? 4 : 0,
        overflow:      'hidden',
        textOverflow:  'ellipsis',
        whiteSpace:    'nowrap',
      }}>
        {card.title ?? card.card_id}
      </div>
      {!card.agent_slug && card.squad && (
        <div style={{ fontSize: 9, color: C.neutralMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {card.squad}
        </div>
      )}
      {card.agent_slug && (
        <div style={{
          fontSize:     9,
          color:        col?.color ?? C.neutralMuted,
          fontFamily:   'monospace',
          overflow:     'hidden',
          textOverflow: 'ellipsis',
          whiteSpace:   'nowrap',
        }}>
          {card.agent_slug}
        </div>
      )}
      {card.tests && card.tests.length > 0 && (
        <div style={{
          display:    'flex',
          alignItems: 'center',
          gap:        4,
          marginTop:  4,
          fontSize:   9,
          color:      C.neutralMuted,
        }}>
          <CheckSquare size={9} aria-hidden="true" style={{ color: C.cyber }} />
          {card.tests.filter((t) => t.status === 'passed').length}/{card.tests.length} testes
        </div>
      )}
    </button>
  );
}

/**
 * KanbanColumn — coluna individual do Kanban.
 */
function KanbanColumn({ column, cards, onCardClick }) {
  return (
    <div
      role="group"
      aria-label={`Coluna ${column.label}: ${cards.length} card${cards.length !== 1 ? 's' : ''}`}
      style={{
        display:         'flex',
        flexDirection:   'column',
        minWidth:        140,
        maxWidth:        180,
        flex:            '1 0 140px',
        backgroundColor: C.blackElev,
        border:          `1px solid ${C.blackBorder}`,
        borderRadius:    8,
        overflow:        'hidden',
      }}
    >
      {/* Header da coluna */}
      <div style={{
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'space-between',
        padding:         '8px 10px',
        backgroundColor: C.blackElev2,
        borderBottom:    `1px solid ${C.blackBorder}`,
        borderTop:       `2px solid ${column.color}`,
      }}>
        <span style={{
          fontSize:      10,
          fontWeight:    700,
          color:         column.color,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          whiteSpace:    'nowrap',
          overflow:      'hidden',
          textOverflow:  'ellipsis',
        }}>
          {column.label}
        </span>
        <span style={{
          fontSize:        10,
          fontWeight:      700,
          color:           cards.length > 0 ? column.color : C.neutralMuted,
          backgroundColor: cards.length > 0 ? `${column.color}22` : C.blackElev,
          padding:         '1px 6px',
          borderRadius:    4,
          minWidth:        18,
          textAlign:       'center',
          flexShrink:      0,
        }}>
          {cards.length}
        </span>
      </div>

      {/* Cards */}
      <div style={{
        flex:       1,
        overflowY:  'auto',
        padding:    '8px',
        minHeight:  60,
      }}>
        {cards.length === 0 ? (
          <div style={{
            fontSize:    10,
            color:       C.neutralMuted,
            textAlign:   'center',
            padding:     '12px 4px',
            fontStyle:   'italic',
          }}>
            vazio
          </div>
        ) : (
          cards.map((card) => (
            <KanbanCard
              key={card.card_id}
              card={card}
              columnId={column.id}
              onClick={onCardClick}
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * PendingGateBanner — destaque de APROVAÇÃO PENDENTE, extraído do Kanban para
 * ficar sempre visível (independente do board). É o que a empresa quer ver em
 * primeiro plano: "há um gate esperando sua decisão".
 */
function PendingGateBanner({ pendingGate, onReviewGate }) {
  const reducedMotion = useReducedMotion();
  if (!pendingGate) return null;
  const label = pendingGate.type;
  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        padding:      '14px 18px',
        borderRadius: 10,
        background:   `linear-gradient(90deg, ${C.digital}26, ${C.digital}08)`,
        border:       `1px solid ${C.digital}66`,
        flexShrink:   0,
        animation:    reducedMotion ? 'none' : 'navaPulse 2s ease-out infinite',
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 9, flexShrink: 0,
        background: `${C.digital}22`, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <ShieldAlert size={19} aria-hidden="true" style={{ color: C.digital }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.digital, letterSpacing: '0.04em' }}>
          APROVAÇÃO PENDENTE
        </div>
        <div style={{ fontSize: 12.5, color: C.digitalLight, marginTop: 2 }}>
          {label
            ? `Gate aguardando sua decisão: ${label}`
            : 'Um Quality Gate aguarda sua aprovação para o fluxo continuar.'}
        </div>
      </div>
      <button
        onClick={() => onReviewGate?.(label)}
        aria-label="Revisar e aprovar o gate pendente"
        style={{
          flexShrink:      0,
          padding:         '10px 18px',
          borderRadius:    8,
          border:          'none',
          backgroundColor: C.digital,
          color:           C.black,
          fontSize:        13,
          fontWeight:      800,
          cursor:          'pointer',
          whiteSpace:      'nowrap',
        }}
      >
        Revisar e aprovar →
      </button>
    </div>
  );
}

/**
 * KanbanBoard — board completo com 8 colunas e aria-live no container.
 * (Oculto por padrão no Live Board — ver SHOW_KANBAN. O banner de gate foi
 * extraído para PendingGateBanner.)
 */
function KanbanBoard({ columns, onCardClick }) {
  const reducedMotion = useReducedMotion();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Grade de colunas */}
      <div
        aria-label="Board Kanban — 8 colunas"
        aria-live="polite"
        aria-relevant="additions text"
        style={{
          display:    'flex',
          gap:        6,
          overflowX:  'auto',
          paddingBottom: 4,
          // Suporte a scroll suave
          scrollBehavior: reducedMotion ? 'auto' : 'smooth',
        }}
      >
        {KANBAN_COLUMNS.map((col) => (
          <KanbanColumn
            key={col.id}
            column={col}
            cards={columns[col.id] ?? []}
            onCardClick={onCardClick}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Tela principal ───────────────────────────────────────────────────────────

/**
 * LiveBoardScreen — tela principal do Live Board.
 * Lê executionId de ?execution=<id> (query string).
 */
export function LiveBoardScreen() {
  const [searchParams] = useSearchParams();
  const executionId = searchParams.get('execution');
  const { navigate } = useNavigationContext();
  const qc = useQueryClient();

  // Board state: hidratado do GET /board + atualizado por eventos SSE kanban
  const [boardColumns, setBoardColumns] = useState(
    () => Object.fromEntries(KANBAN_COLUMNS.map((c) => [c.id, []])),
  );
  const [boardHydrated, setBoardHydrated] = useState(false);

  // Card selecionado para o modal
  const [selectedCard, setSelectedCard] = useState(null); // { card, columnId }

  // Dados da execução e stream SSE. Enquanto a execução NÃO está terminal, polla
  // o status (leve) para detectar o fim e encerrar o polling dos gates.
  const execPoll = useCallback(
    (query) => (EXEC_TERMINAL.has(String(query.state.data?.status ?? '').toLowerCase()) ? false : 8_000),
    [],
  );
  const { data: execution } = useExecution(executionId, { refetchInterval: execPoll });
  const { data: boardData, isSuccess: boardLoaded } = useExecutionBoard(executionId);

  // Parar a execução ao vivo (corta o gasto de tokens na hora).
  const cancelExec = useCancelExecution();
  const stopExecution = useCallback(async () => {
    if (!executionId) return;
    if (!window.confirm('Parar esta execução agora? O pipeline é encerrado imediatamente.')) return;
    try {
      await cancelExec.mutateAsync(executionId);
      qc.invalidateQueries({ queryKey: executionKeys.detail(executionId) });
    } catch { /* erro no estado da mutation */ }
  }, [executionId, cancelExec, qc]);

  // Aba do painel principal: o log (padrão) ou a Saúde. A consulta é a mesma da aba
  // (mesma chave no cache) — aqui só serve ao selo de alertas críticos.
  // `?aba=saude` (atalho do Dashboard e da Conferência) abre direto na Saúde
  const [aba, setAba] = useState(searchParams.get('aba') === 'saude' ? 'saude' : 'log');
  // com a aba fechada só o selo importa: consulta a cada 30 s, não a cada 10
  const criticosDaSaude = useSaudeDaExecucao(executionId, { intervalo: aba === 'saude' ? 10_000 : 30_000 })
    .data?.resumo?.alertas?.critico ?? 0;

  const {
    connectionState,
    cliLines,
    kanbanEvents,
    metrics,
    gateEvent,
    sandboxEvent,
    streamError,
  } = useExecutionStream(executionId);

  // Estado real dos gates. Fallback ROBUSTO ao SSE: o stream não reemite o gate
  // ao reconectar e, atrás de proxy/URL pública, o evento 'gate_*_pending' pode
  // nem chegar — então o banner só aparecia após refresh manual. `gatePollInterval`
  // polla ENQUANTO a execução roda (descobre o gate recém-'pending' sem SSE) e
  // para quando o gate é decidido OU a execução termina. Ver gatePollInterval().
  const execStatus = execution?.status;
  const pollGates = useCallback(
    (query) => gatePollInterval(query.state.data?.status, execStatus),
    [execStatus],
  );
  const gateAQuery = useExecutionGateA(executionId, { refetchInterval: pollGates });
  const gateBQuery = useExecutionGateB(executionId, { refetchInterval: pollGates });

  // Gate pendente = evento SSE ao vivo OU estado consultado (fallback robusto).
  const pendingGate = useMemo(() => {
    if (gateEvent?.payload?.status === 'pending') {
      return { type: gateEvent.payload.gate_type ?? 'gate' };
    }
    if (gateBQuery.data?.status === 'pending') return { type: 'gate_b' };
    if (gateAQuery.data?.status === 'pending') return { type: 'gate_a' };
    return null;
  }, [gateEvent, gateBQuery.data, gateAQuery.data]);

  // Ao clicar em "Revisar e aprovar", leva para a tela de aprovação do gate
  // (Gate B → /gates/b; Gate A → /gates/negocio), passando a execução. É lá que
  // Admin/Manager (approve_gate) revisam o plano e aprovam.
  const handleReviewGate = (gateType) => {
    const gt = String(gateType ?? '').toLowerCase();
    const route = gt.includes('b') ? '/gates/b' : '/gates/negocio';
    navigate(`${route}?execution=${executionId}`);
  };

  // ── Ciclos ao vivo (#3) ─────────────────────────────────────────────────────
  // O Live Board parte do executionId; project_id agora vem no status da execução.
  const projectId = execution?.project_id ?? null;
  const backlogQuery = useBacklog(projectId);
  const cycles = backlogQuery.data?.cycles ?? [];

  // Hidrata o board com dados do GET /board (estado inicial)
  useEffect(() => {
    if (boardLoaded && boardData && !boardHydrated) {
      setBoardColumns(buildInitialBoard(boardData));
      setBoardHydrated(true);
    }
  }, [boardLoaded, boardData, boardHydrated]);

  // Aplica eventos SSE kanban ao estado do board
  useEffect(() => {
    if (kanbanEvents.length === 0) return;
    const lastEvent = kanbanEvents[kanbanEvents.length - 1];
    setBoardColumns((prev) => applyKanbanEvent(prev, lastEvent));
  }, [kanbanEvents]);

  // Ao receber QUALQUER evento de gate pelo SSE (pending OU decided), revalida as
  // fontes autoritativas — assim o status de aprovação reflete no Live Board
  // instantaneamente, sem o usuário precisar dar refresh na página. O backend
  // emite 'gate_a_decided'/'gate_b_decided' após o signal Temporal ser processado.
  useEffect(() => {
    if (!gateEvent || !executionId) return;
    qc.invalidateQueries({ queryKey: executionKeys.gateA(executionId) });
    qc.invalidateQueries({ queryKey: executionKeys.gateB(executionId) });
    qc.invalidateQueries({ queryKey: executionKeys.detail(executionId) });
    if (projectId) {
      qc.invalidateQueries({ queryKey: ['project', projectId, 'journey'] });
    }
  }, [gateEvent, qc, executionId, projectId]);

  // Progresso derivado do último evento metrics
  const progress = metrics?.progress_pct ?? 0;

  const handleCardClick = useCallback((card, columnId) => {
    setSelectedCard({ card, columnId });
  }, []);

  const handleCloseModal = useCallback(() => setSelectedCard(null), []);

  // Kanban visível por padrão (igual ao protótipo); toggle p/ quem prefere só logs.
  const [showKanban, setShowKanban] = useState(true);
  // Board a exibir: o ao vivo (SSE/GET /board) quando tem cards; senão, semeado do
  // Product Backlog aprovado — nunca aparece vazio e reflete o escopo real.
  const seededColumns = useMemo(() => seedBoardFromBacklog(backlogQuery.data), [backlogQuery.data]);
  const liveCardCount = useMemo(
    () => Object.values(boardColumns).reduce((a, c) => a + c.length, 0), [boardColumns]);
  const displayColumns = liveCardCount > 0 ? boardColumns : seededColumns;
  const boardCardCount = useMemo(
    () => Object.values(displayColumns).reduce((a, c) => a + c.length, 0), [displayColumns]);
  const squadCount = FLOW_NODES.filter((n) => n.tier === 'SQUAD').length;
  const epicLabel = backlogQuery.data?.epics?.[0]?.title || execution?.project_name || 'execução';

  // ── Sem execução selecionada ───────────────────────────────────────────────
  if (!executionId) {
    return (
      <div style={{ padding: 32 }}>
        <div style={{
          display:       'flex',
          flexDirection: 'column',
          alignItems:    'center',
          gap:           16,
          padding:       48,
          textAlign:     'center',
        }}>
          <Activity size={44} aria-hidden="true" style={{ color: C.blackBorder }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: C.neutralMuted }}>
            Nenhuma execução selecionada
          </div>
          <div style={{ fontSize: 13, color: C.neutralMuted, maxWidth: 380 }}>
            Abra o Live Board a partir da Jornada ou do Dashboard, ou passe
            o parâmetro <code style={{ color: C.cyber }}>?execution=&lt;id&gt;</code> na URL.
          </div>
          <Btn variant="ghost" size="sm" onClick={() => navigate('dashboard')}>
            Ir ao Dashboard
          </Btn>
        </div>
      </div>
    );
  }

  // ── Erro permanente de auth ────────────────────────────────────────────────
  if (streamError?.isForbidden) {
    return (
      <div style={{ padding: 24 }}>
        <StreamErrorBanner error={streamError} />
      </div>
    );
  }

  // ── Layout principal ──────────────────────────────────────────────────────
  const projectName     = execution?.project_name ?? 'Execução em andamento';
  // Backend usa 'done' para concluído; normaliza para 'completed' (contrato da UI).
  const _rawStatus      = execution?.status ?? 'running';
  const executionStatus = _rawStatus === 'done' ? 'completed' : _rawStatus;
  // Ciclo atual (multi-ciclo): derivado das linhas de log ("🔄 Ciclo N/M …").
  const currentCycle = useMemo(() => {
    for (let i = cliLines.length - 1; i >= 0; i -= 1) {
      const m = cliLines[i].match(/Ciclo\s+(\d+)\s*\/\s*(\d+)/);
      if (m) return { n: m[1], total: m[2] };
    }
    return null;
  }, [cliLines]);

  // Entrega CONSOLIDADA: o codegen gera TODOS os ciclos numa passada única
  // ("🎯 entrega completa (N ciclos consolidados)"). Sem detectar isto, o strip
  // mostrava "Ciclo 1/N" com o resto pendente — parecia que ciclos foram pulados.
  const consolidatedTotal = useMemo(() => {
    for (let i = cliLines.length - 1; i >= 0; i -= 1) {
      const m = cliLines[i].match(/\((\d+)\s+ciclos?\s+consolidad/i);
      if (m) return Number(m[1]);
    }
    return null;
  }, [cliLines]);
  const isConsolidated = consolidatedTotal != null;
  const cyclesDone = isConsolidated && executionStatus === 'completed';

  return (
    <>
      {/* Modal de detalhes do card */}
      {selectedCard && (
        <CardDetailModal
          card={selectedCard.card}
          columnId={selectedCard.columnId}
          onClose={handleCloseModal}
        />
      )}

      <div style={{
        display:       'flex',
        flexDirection: 'column',
        gap:           0,
        height:        '100%',
        minHeight:     0,
      }}>

        {/* ── Topbar ──────────────────────────────────────────────────── */}
        <div style={{
          display:         'flex',
          alignItems:      'center',
          gap:             16,
          padding:         '12px 20px',
          backgroundColor: C.blackElev,
          borderBottom:    `1px solid ${C.blackBorder}`,
          flexShrink:      0,
          flexWrap:        'wrap',
        }}>
          {/* Identidade */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 160 }}>
            <Radio size={16} aria-hidden="true" style={{ color: C.cyber }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.neutral }}>
                {projectName}
              </div>
              <div style={{ fontSize: 10, color: C.neutralMuted }}>
                #{executionId.slice(0, 8)}
              </div>
            </div>
            {isConsolidated ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
                fontWeight: 700, color: C.quantum ?? C.cyber,
                background: `${C.quantum ?? C.cyber}18`, border: `1px solid ${C.quantum ?? C.cyber}44`,
                borderRadius: 12, padding: '2px 9px' }}
                title="Entrega única cobrindo todos os ciclos do backlog">
                {cyclesDone ? '✅' : '🎯'} {consolidatedTotal} ciclos · {cyclesDone ? 'consolidados' : 'consolidando'}
              </span>
            ) : currentCycle && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
                fontWeight: 700, color: C.quantum ?? C.cyber,
                background: `${C.quantum ?? C.cyber}18`, border: `1px solid ${C.quantum ?? C.cyber}44`,
                borderRadius: 12, padding: '2px 9px' }}
                title="Ciclo de desenvolvimento atual">
                🔄 Ciclo {currentCycle.n}/{currentCycle.total}
              </span>
            )}
          </div>

          {/* Métricas inline */}
          {metrics && (
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              {metrics.tokens_consumed != null && (
                <span style={{ fontSize: 11, color: C.neutralDim }}>
                  <span style={{ color: C.cyber, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {Number(metrics.tokens_consumed).toLocaleString('pt-BR')}
                  </span>
                  {' '}tk
                </span>
              )}
              {metrics.cost_usd != null && (
                <span style={{ fontSize: 11, color: C.neutralDim }}>
                  <span style={{ color: C.neon, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    US$ {Number(metrics.cost_usd).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </span>
              )}
            </div>
          )}

          {/* Indicador de conexão */}
          <ConnectionIndicator state={connectionState} />

          {/* Indicador de sandbox na topbar — exibe quando sandbox ativo (F4-FE-3) */}
          {sandboxEvent && (() => {
            const { status, is_real } = sandboxEvent.payload ?? {};
            const isActive = status === 'spawned' || status === 'ready';
            if (!isActive) return null;
            const isReal      = Boolean(is_real);
            const accentColor = isReal ? C.cyber : C.digital;
            return (
              <span
                role="status"
                aria-label={`Sandbox ${isReal ? 'real' : 'simulado'} ativo`}
                style={{
                  display:         'inline-flex',
                  alignItems:      'center',
                  gap:             5,
                  fontSize:        10,
                  fontWeight:      700,
                  letterSpacing:   '0.06em',
                  color:           accentColor,
                  padding:         '3px 8px',
                  borderRadius:    4,
                  backgroundColor: `${accentColor}18`,
                  border:          `1px solid ${accentColor}44`,
                }}
              >
                <Box size={11} aria-hidden="true" />
                {isReal ? 'SANDBOX REAL' : 'SIMULADO'}
              </span>
            );
          })()}

          {/* Ação contextual */}
          {executionStatus === 'completed' && (
            <Btn
              variant="primary"
              size="sm"
              icon={CheckCircle}
              onClick={() => navigate('deliveryCheck')}
            >
              Conferir Entrega
            </Btn>
          )}
          {!EXEC_TERMINAL.has(String(executionStatus).toLowerCase()) && (
            <Btn
              variant="danger"
              size="sm"
              icon={Square}
              onClick={stopExecution}
              disabled={cancelExec.isPending}
              aria-label="Parar esta execução"
            >
              {cancelExec.isPending ? 'Parando...' : 'Parar execução'}
            </Btn>
          )}
        </div>

        {/* ── Barra de progresso global ─────────────────────────────── */}
        <div style={{
          padding:         '8px 20px',
          backgroundColor: C.blackElev,
          flexShrink:      0,
          borderBottom:    `1px solid ${C.blackBorder}`,
        }}>
          <ProgressBar progress={progress} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{ fontSize: 10, color: C.neutralMuted }}>
              {rotuloDoStatus(executionStatus)}
            </span>
            <span style={{ fontSize: 10, color: C.neutral, fontVariantNumeric: 'tabular-nums' }}>
              {Math.round(progress)}%
            </span>
          </div>
          {/* O motivo do bloqueio (Onda 2): quais bloqueios do contrato ficaram sem aceite. */}
          {executionStatus === 'contract_blocked' && execution?.error && (
            <div role="alert" style={{ fontSize: 11, color: C.hot, marginTop: 4, lineHeight: 1.4 }}>
              {execution.error}
            </div>
          )}
          {/* Strip de ciclos ao vivo (#3) — stepper com o ciclo atual em destaque.
              Consolidado: todos os ciclos numa entrega única (✓ ao concluir). */}
          <CycleStrip
            cycles={cycles}
            current={currentCycle ? Number(currentCycle.n) : undefined}
            consolidated={isConsolidated}
            allDone={cyclesDone}
          />
        </div>

        {/* O painel "QUALITY GATES (5) · aprove aqui mesmo" saiu daqui em 2026-10-06 (decisão
            do dono). O gate que PARA a execução continua aprovável nesta tela pelo banner de
            aprovação pendente ("Revisar e aprovar →"); os gates da jornada, na Gestão do
            Projeto e na Fila de Gates. */}

        {/* ── Erro de stream (não permanente) ──────────────────────── */}
        {streamError && !streamError.isForbidden && (
          <div style={{ padding: '8px 20px', flexShrink: 0 }}>
            <StreamErrorBanner error={streamError} />
          </div>
        )}

        {/* ── Corpo principal em 3 colunas ───────────────────────── */}
        {/* ── Corpo principal: COLUNA ÚNICA ──────────────────────────
            Decisão do dono (2026-09-22): as colunas "Executing Flow" (esquerda) e
            "Métricas ao vivo" (direita) saíram, e o que ficou — Kanban + log de execução
            — ocupa a largura inteira. O log virou o protagonista quando cada etapa passou
            a publicar nele progresso, custo e provas; disputar espaço com duas colunas
            laterais era o que o deixava estreito. Tokens e custo seguem na topbar, então
            nenhuma informação se perdeu. */}
        <div style={{
          display:               'grid',
          gridTemplateColumns:   '1fr',
          gap:                   1,
          flex:                  1,
          minHeight:             0,
          overflow:              'hidden',
        }}>

          {/* ── Coluna Central: Kanban 8 colunas + CLI ─────────── */}
          {/* overflow:hidden (não auto): o CliLog (flex:1) preenche a coluna e é o
              CORPO do log que rola internamente — assim o layout se ajusta à tela
              em vez de esticar/rolar a coluna toda. */}
          <div style={{
            display:         'flex',
            flexDirection:   'column',
            gap:             12,
            padding:         14,
            overflow:        'hidden',
            minHeight:       0,
            backgroundColor: C.black,
          }}>

            {/* Aprovação pendente — destaque no topo do stream (o que a empresa
                quer ver primeiro: um gate esperando decisão). */}
            <PendingGateBanner pendingGate={pendingGate} onReviewGate={handleReviewGate} />

            {/* Abas do painel principal: o log de sempre ou a Saúde da execução. */}
            <div role="tablist" aria-label="Painel da execução"
              style={{ display: 'flex', gap: 4, flexShrink: 0, borderBottom: `1px solid ${C.blackBorder}` }}>
              {[['log', 'Execução'], ['saude', 'Saúde']].map(([id, rotulo]) => (
                <button key={id} type="button" role="tab" id={`aba-${id}`} aria-selected={aba === id}
                  aria-controls={`painel-${id}`} onClick={() => setAba(id)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                    borderBottom: `2px solid ${aba === id ? C.cyber : 'transparent'}`,
                    color: aba === id ? C.neutral : C.neutralDim, fontSize: 13, fontWeight: 600,
                    padding: '8px 14px', minHeight: 36, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {rotulo}
                  {id === 'saude' && criticosDaSaude > 0 && (
                    <span data-testid="selo-alertas-criticos"
                      aria-label={`${criticosDaSaude} ${criticosDaSaude === 1 ? 'alerta crítico' : 'alertas críticos'}`}
                      style={{ background: C.hot, color: C.black, borderRadius: 10, fontSize: 11,
                        fontWeight: 700, padding: '1px 7px', fontVariantNumeric: 'tabular-nums' }}>
                      {criticosDaSaude.toLocaleString('pt-BR')}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {aba === 'saude' && (
              <div role="tabpanel" id="painel-saude" aria-labelledby="aba-saude"
                style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <SaudeDaExecucao key={executionId} executionId={executionId} projectId={projectId}
                  onParar={stopExecution} parando={cancelExec.isPending} />
              </div>
            )}

            {/* O log e o Kanban seguem montados na outra aba (só escondidos): a rolagem e o
                estado do log não se perdem ao alternar. */}
            <div role="tabpanel" id="painel-log" aria-labelledby="aba-log"
              style={{ display: aba === 'log' ? 'contents' : 'none' }}>

            {/* Kanban (8 colunas) — peça central do board (igual ao protótipo),
                acima dos logs. Toggle p/ quem prefere só os logs. */}
            <section aria-labelledby="kanban-heading" style={{ flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 8px' }}>
                <Layers size={13} aria-hidden="true" style={{ color: C.cyber }} />
                <h2 id="kanban-heading" style={{
                  fontSize: 12, fontWeight: 700, color: C.neutral, margin: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Live Board <span style={{ color: C.neutralMuted, fontWeight: 500 }}>· {epicLabel}</span>
                </h2>
                <span style={{ marginLeft: 'auto', fontSize: 10.5, color: C.neutralMuted, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {squadCount} agents · {boardCardCount} tasks
                </span>
                <button
                  type="button"
                  onClick={() => setShowKanban((v) => !v)}
                  aria-label={showKanban ? 'Ocultar board' : 'Mostrar board'}
                  style={{ fontSize: 10, color: C.neutralDim, background: 'transparent',
                    border: `1px solid ${C.blackBorder}`, borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}
                >
                  {showKanban ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              {showKanban && (
                <div style={{ maxHeight: '26vh', overflowY: 'auto' }}>
                  <KanbanBoard columns={displayColumns} onCardClick={handleCardClick} />
                </div>
              )}
              <div style={{ height: 1, backgroundColor: C.blackBorder, margin: '12px 0', flexShrink: 0 }} />
            </section>

            {/* Logs de Execução — PROTAGONISTA. Ocupa todo o espaço; o usuário
                acompanha TUDO ao vivo (inclui os logs da geração de código). */}
            <section
              aria-label="Logs de execução"
              style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
            >
              <CliLog lines={cliLines} connectionState={connectionState} />
            </section>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
